/*
* Copyright  2025-2026, Airspan
* All Rights Reserved
*
* This code is unpublished, confidential and proprietary to Airspan and may only be used in accordance with the appropriate
* license agreement. All other copying, modification, distribution, or
* reverse engineering are strictly prohibited.
*/

import { useSelector } from "react-redux";
import { API, DIALOGOPENFOR } from "../../../../globals/globals";
import { useAddLabelDevicesMutation, useGetAllDevicesByGroupQuery, useGetNodesQuery, useRemoveLabelDevicesMutation } from "../../../../store/api/devices/deviceApiSlice";
import { useEffect, useState, useRef } from "react";
import { IGeoRegion } from "../../../../store/api/admin/types";
import { useStoreDispatch } from "../../../../store/hooks";
import { GridRowId } from "@mui/x-data-grid";
import {
    addDeviceSiteGroupSelector, addDeviceGeoRegionSelector, geoRegionSelector,
    siteGroupSelector, urlParameterSelector, isAssociatedDevicesRemovedSelector,
    isLabelDeviceAssociationProgressSelector
} from "../../../../store/filters/devices/Label/LabelDeviceFilterSelectors";
import { setBasicDialogOpenFor, setConfirmDialog, setOpenToast } from "../../../../store/helpers/helperReducers";
import { basicDialogSelector, confirmDialogSelector, paginationModelSelector } from "../../../../store/helpers/helperSelectors";
import { errorMessage } from "../../../../utils/helpers";
import { IFilterSelectBox } from "../../../Shared/Filter/types";
import { useRegionDisplay } from "../../../../pages/Administrations/AccessControls/Regions/hooks";
import { AppState } from "../../../../store";
import { initialState } from "../../../../store/helpers/helperInitialState";
import { searchKeywordSelector } from "../../../../store/search/selector";
import { resetKeyword } from "../../../../store/search/reducer";
import { labelNameSelector } from "../../../../store/filters/devices/Label/LabelDeviceFilterSelectors";
import { ILabelData } from "../../../../store/api/devices/types";
import { setAddDeviceGeoRegion, setAddDeviceSiteGroup, setGeoRegion, setIsAssociatedDevicesRemoved, setIsLabelDeviceAssociationProgress, setSiteGroup } from "../../../../store/filters/devices/Label/LabelDeviceFilterReducer";



export const useDevices = () => {
    const dispatch = useStoreDispatch();

    // Always use Redux state directly — no local copy!
    const label = useSelector(labelNameSelector);

    const isLabelDeviceAssociationProgress = useSelector(isLabelDeviceAssociationProgressSelector);
    const basicDialogOpenFor = useSelector(basicDialogSelector);
    const openConfirmDialog = useSelector(confirmDialogSelector);
    const filters = useSelector(urlParameterSelector);
    const siteGroup = useSelector(siteGroupSelector);
    const selectedGeoRegion = useSelector<AppState, IGeoRegion>(geoRegionSelector);
    const isAssociatedDevicesRemoved = useSelector(isAssociatedDevicesRemovedSelector);
    const addDeviceSiteGroup = useSelector(addDeviceSiteGroupSelector);
    const selectedAddDeviceGeoRegion = useSelector<AppState, IGeoRegion>(addDeviceGeoRegionSelector);

    const [removeLabelFromDevice] = useRemoveLabelDevicesMutation();
    const { devices = initialState.pagination } = useSelector(paginationModelSelector);
    const [selectedDeviceRowIds, setSelectedDeviceRowIds] = useState<GridRowId[]>([]);
    const [deviceDisassociationLoader, setDeviceDisassociationLoader] = useState(false);
    const [sortOrder, setSortOrderToNode] = useState("name, asc");

    const { geoRegions } = useRegionDisplay();
    const regionOptions = geoRegions.map((region) => ({ value: region.objectKey, text: region.name }));

    const keyword = useSelector(searchKeywordSelector);
    filters.set("sort", sortOrder);
    filters.set("page", devices.page.toString());
    filters.set("size", devices.pageSize.toString());
    filters.set("search", keyword);
    const args = { filters: filters.toString() };

    const { data, isLoading, refetch } = useGetNodesQuery(args);

    const rowsData = data?.data || [];
    const rowsCount = data?.totalItems || 0;

    const siteGroups = selectedGeoRegion.objectKey
        ? geoRegions.find((x) => x.objectKey === selectedGeoRegion.objectKey)?.siteGroups || []
        : geoRegions.flatMap((x) => x.siteGroups || []);

    const siteGroupOptions = Array.from(new Set(siteGroups.map((sg: any) => sg.name)))
        .map((name) => ({ value: name, text: name }));

    const addSiteGroups = selectedAddDeviceGeoRegion.objectKey
        ? geoRegions.find((region: IGeoRegion) => region.objectKey === selectedAddDeviceGeoRegion.objectKey)?.siteGroups || []
        : geoRegions.flatMap((region: IGeoRegion) => region.siteGroups || []);

    const addSiteGroupOptions = Array.from(new Set(addSiteGroups.map((siteGroup: any) => siteGroup.name)))
        .map((name) => ({ value: name, text: name }));
    const addRegionOptions = geoRegions.map((region) => ({ value: region.objectKey, text: region.name }));

    const prevGeoRegion = useRef(selectedGeoRegion.objectKey);
    useEffect(() => {
        if (prevGeoRegion.current !== selectedGeoRegion.objectKey) {
            dispatch(setSiteGroup(""));
            prevGeoRegion.current = selectedGeoRegion.objectKey;
        }
    }, [selectedGeoRegion.objectKey, dispatch]);

    const prevAddGeoRegion = useRef(selectedAddDeviceGeoRegion.objectKey);
    useEffect(() => {
        if (prevAddGeoRegion.current !== selectedAddDeviceGeoRegion.objectKey) {
            dispatch(setAddDeviceSiteGroup(""));
            prevAddGeoRegion.current = selectedAddDeviceGeoRegion.objectKey;
        }
    }, [selectedAddDeviceGeoRegion.objectKey, dispatch]);

    const searchFilters: IFilterSelectBox[] = [
        {
            id: "geoRegion",
            label: "All Regions",
            value: selectedGeoRegion.objectKey,
            setValue: setGeoRegion,
            values: regionOptions,
            width: "123px",
        },
        {
            id: "group",
            label: "All Groups",
            value: siteGroup || "",
            setValue: setSiteGroup,
            values: siteGroupOptions,
            width: "119px",
        },
    ];

    const searchAddFilters: IFilterSelectBox[] = [
        {
            id: "addDeviceGeoRegion",
            label: "All Regions",
            value: selectedAddDeviceGeoRegion.objectKey,
            setValue: setAddDeviceGeoRegion,
            values: addRegionOptions,
            width: "123px",
        },
        {
            id: "addDeviceGroup",
            label: "All Groups",
            value: addDeviceSiteGroup || "",
            setValue: setAddDeviceSiteGroup,
            values: addSiteGroupOptions,
            width: "119px",
        },
    ];

    // after remove 
    useEffect(() => {
        if (isAssociatedDevicesRemoved) {
            refetch();
            dispatch(setIsAssociatedDevicesRemoved(false)); //  RESET FLAG
        }
    }, [isAssociatedDevicesRemoved]);

    // after add
    useEffect(() => { refetch(); }, [isLabelDeviceAssociationProgress, isAssociatedDevicesRemoved === true]);

    const openRemoveConfirmDialog = () => {
        dispatch(setConfirmDialog(DIALOGOPENFOR.REMOVELABELDEVICES));
    };



    const handleRemoveAction = async (actionName: string, handleCancel: () => void) => {
        if (!actionName || !actionName.match("yes")) return;
        return await removeDeviceAssociationHandler();
    };

    const removeDeviceAssociationHandler = async () => {

        if (!label)
            return;

        try {
            setDeviceDisassociationLoader(true);
            const response = await removeLabelFromDevice({
                labelName: label.value,
                deviceKeys: selectedDeviceRowIds as string[],
            });

            if ('data' in response && response.data?.status === 200) {
                const response = await refetch();
                setSelectedDeviceRowIds([]);
                setDeviceDisassociationLoader(false);
                if (response.data) {
                    dispatch(
                        setOpenToast({
                            openToast: true,
                            toastType: "success",
                            message: `Device${selectedDeviceRowIds.length > 1 ? 's have' : ' has'} been removed from label: ${label.text}.`,
                        })
                    );
                }
            }
            else if ('error' in response && response?.error)
                throw response?.error;
        }
        catch (error) {
            setDeviceDisassociationLoader(false);
            dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
        }
        setSelectedDeviceRowIds([]);
    };


    const onRowSelectedHandler = (rowIds: GridRowId[]) => {
        setSelectedDeviceRowIds(rowIds);
    };

    const addDeviceHandler = () => {
        dispatch(setIsLabelDeviceAssociationProgress(true));
        dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.ADDLABELDEVICE));
    };

    return {
        dispatch,
        isLoading,
        rowsData,
        rowsCount,
        label,  // <-- redux-controlled label
        selectedDeviceRowIds,
        setSelectedDeviceRowIds,
        searchFilters,
        searchAddFilters,
        deviceDisassociationLoader,
        setSortOrderToNode,
        onRowSelectedHandler,
        handleRemoveAction,
        removeDeviceAssociationHandler,
        basicDialogOpenFor,
        openConfirmDialog,
        addDeviceHandler,
        openRemoveConfirmDialog
    };
};

export const useAddDevice = () => {

    const dispatch = useStoreDispatch();
    const label = useSelector(labelNameSelector);
    const isLabelDeviceAssociationProgress = useSelector(isLabelDeviceAssociationProgressSelector);
    const addDeviceSiteGroup = useSelector(addDeviceSiteGroupSelector);
    const selectedAddDeviceGeoRegion = useSelector<AppState, IGeoRegion>(addDeviceGeoRegionSelector);
    const { addDevice = initialState.pagination } = useSelector(paginationModelSelector);
    const filters = useSelector(urlParameterSelector);
    const keyword = useSelector(searchKeywordSelector);
    const [addLabelToDevices] = useAddLabelDevicesMutation();

    const [selectedDeviceRowIds, setSelectedDeviceRowIds] = useState<GridRowId[]>([]);
    const [deviceAssociationLoader, setDeviceAssociationLoader] = useState(false);
    const [sortOrder, setSortOrderToNode] = useState("name, asc");
    const { geoRegions } = useRegionDisplay();

    filters.set("sort", sortOrder);
    filters.set("page", addDevice.page.toString());
    filters.set("size", addDevice.pageSize.toString());
    filters.set("search", keyword);

    const args = { filters: filters.toString() };
    const { data, isLoading, refetch } = useGetNodesQuery(args);
    const { data: allDevicesData } = useGetAllDevicesByGroupQuery(args);

    const rowsData = data?.data || [];
    const rowsCount = data?.totalItems || 0;
    const siteGroups = geoRegions.find((region: IGeoRegion) => region.objectKey === selectedAddDeviceGeoRegion.objectKey)?.siteGroups || [];
    const siteGroupOptions = siteGroups.map((siteGroup: any) => ({ value: siteGroup.name, text: siteGroup.name }));
    const regionOptions = geoRegions.map((region) => ({ value: region.objectKey, text: region.name }));

    const searchFilters: IFilterSelectBox[] = [
        {
            id: "addDeviceGroup",
            label: "GROUP",
            value: addDeviceSiteGroup || "",
            setValue: setAddDeviceSiteGroup,
            values: siteGroupOptions,
            width: "200px",
        },
        {
            id: "addDeviceGeoRegion",
            label: "REGION",
            value: selectedAddDeviceGeoRegion.objectKey,
            setValue: setAddDeviceGeoRegion,
            values: regionOptions,
            width: "200px",
        },
    ];

    const closeAddDeviceDialogHandler = (label: ILabelData) => {
        dispatch(setIsLabelDeviceAssociationProgress(false));
        dispatch(setBasicDialogOpenFor(undefined));
        dispatch(resetKeyword());
    };

    const addLabelDeviceAssociationHandler = async (isAddAll: boolean = false) => {
        if (!label)
            return;

        try {
            setDeviceAssociationLoader(true);
            const response = await addLabelToDevices({
                labelName: label.value,
                deviceKeys: isAddAll
                    ? allDevicesData?.map(device => device.objectKey) ?? []
                    : (selectedDeviceRowIds as string[]),
            });

            if ('data' in response && (response.data?.status === 200 || response.data?.status === 207)) {
                setDeviceAssociationLoader(false);
                refetch();
                setSelectedDeviceRowIds([]);
                dispatch(
                    setOpenToast({
                        openToast: true,
                        toastType: "success",
                        message: `Device${selectedDeviceRowIds.length > 1 ? 's have' : ' has'} successfully added to group: ${label.text}.`,
                    })
                );
                setTimeout(() => {
                    closeAddDeviceDialogHandler(label);
                }, 500);

            }
            else if ('error' in response && response?.error)
                throw response?.error;
        }
        catch (error) {
            setDeviceAssociationLoader(false);
            dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
        }
    };

    const onRowSelectedHandler = (rowIds: GridRowId[]) => {
        setSelectedDeviceRowIds(rowIds);
    };

    return {
        dispatch,
        rowsData,
        rowsCount,
        isLoading,
        selectedDeviceRowIds,
        setSelectedDeviceRowIds,
        searchFilters,
        deviceAssociationLoader,
        setSortOrderToNode,
        addLabelDeviceAssociationHandler,
        closeAddDeviceDialogHandler,
        onRowSelectedHandler,
    };
};


